/**
 * "Casino account" role for display-only UI (e.g. the footer CASINO watermark).
 *
 * A casino account is a login made with Create Casino Account (ms-auth sets
 * Mongo users.isCasinoAccount = true and assigns the casino's Only_{shortName}
 * Cognito group). Login / GetMe return that flag on the user, and the PWA keeps
 * it as persistedState.userInfo.isCasinoAccount, the same flag the Events tab,
 * Challenges, Join Challenge and the pending challenge card already use.
 *
 * Only an explicit `true` counts: missing / null / false are not a casino
 * account. Display-only; no casino lists, no data-path changes.
 */
import { useSelector } from 'react-redux';

export type CasinoAccountRoleUser = {
  isCasinoAccount?: boolean | null;
};

export type CasinoAccountRoleState = {
  app: { persistedState: { userInfo?: CasinoAccountRoleUser | null } };
};

/** True only when the signed-in user is a casino account. */
export function hasCasinoAccountRole(userInfo?: CasinoAccountRoleUser | null): boolean {
  return userInfo?.isCasinoAccount === true;
}

/** Redux selector: `useSelector(selectHasCasinoAccountRole)`. */
export const selectHasCasinoAccountRole = (state: CasinoAccountRoleState): boolean =>
  hasCasinoAccountRole(state?.app?.persistedState?.userInfo);

/** Footer watermark word. */
export const CASINO_TAB_BAR_WATERMARK_TEXT = 'CASINO';
/** Modifier on the IonTabBar that draws the watermark (_m-casino-tabs.scss). */
export const CASINO_TAB_BAR_WATERMARK_CLASS = 'casino-tab-bar--watermark';

export type CasinoTabBarWatermark = {
  /** Extra class for the IonTabBar, or undefined when there is no watermark. */
  className: string | undefined;
  /** Value for the IonTabBar `data-watermark` attribute (the CSS draws it). */
  text: string | undefined;
};

/** Tab bar watermark props: only for a casino account. */
export function casinoTabBarWatermark(hasRole: boolean): CasinoTabBarWatermark {
  return hasRole
    ? { className: CASINO_TAB_BAR_WATERMARK_CLASS, text: CASINO_TAB_BAR_WATERMARK_TEXT }
    : { className: undefined, text: undefined };
}

/** Watermark props for the casino tab bar, from the signed-in user's role. */
export function useCasinoTabBarWatermark(): CasinoTabBarWatermark {
  const hasRole = useSelector(selectHasCasinoAccountRole);
  return casinoTabBarWatermark(hasRole);
}
